/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Popover, Text, TextLink } from '@oneaudi/unified-web-components';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTrackingManager } from '../../context/useTrackingManager';
import { useNumberFormatter } from '../../context/Context';
import { useInitialState } from '../../context/InitialStateContext';
import { matchI18n, useI18n } from '../../i18n';

export const StyledPopoverMaxWidthContainer = styled.div`
  max-width: 430px; // as aligned with Design Team
  overflow: hidden;
`;

export interface StockCar {
  readonly url: string;
  readonly amount: number;
}

export interface VtpPopoverProps {
  readonly usedCars: StockCar;
  readonly newCars: StockCar;
  readonly carsInStock: number;
  readonly carlineGroup: string;
  readonly carline: string;
}

const VtpPopoverPopover: React.FC<VtpPopoverProps> = ({
  newCars,
  usedCars,
  carsInStock,
  carlineGroup,
  carline,
}) => {
  const { i18nMessages } = useInitialState();

  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { trackCTAClick, trackExpandVTPLayerClick } = useTrackingManager();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const popoverTitle = useI18n('newAndUsedCarsPopoverTitle');
  const popoverDescription = useI18n('newAndUsedCarsPopoverDescriptionText');

  const newCarsMarketText = matchI18n(
    'newAndUsedCarsPopoverCTANewCarsMarketTextWithValue',
    i18nMessages,
    {
      value: useNumberFormatter(newCars ? newCars.amount : 0).toString(),
    },
  );

  const usedCarsMarketText = matchI18n(
    'newAndUsedCarsPopoverCTAUsedCarsMarketTextWithValue',
    i18nMessages,
    {
      value: useNumberFormatter(usedCars ? usedCars.amount : 0).toString(),
    },
  );

  const availableCarsInStockText = matchI18n('newAndUsedCarsCTALabel', i18nMessages, {
    value: useNumberFormatter(carsInStock).toString(),
  });

  useEffect(() => {
    if (popoverOpen)
      trackExpandVTPLayerClick(
        matchI18n('newAndUsedCarsPopoverTitle', i18nMessages),
        `${carlineGroup}~${carline}`,
      );
  }, [popoverOpen]);

  const handlePopoverOpen = () => {
    setPopoverOpen(true);
    trackExpandVTPLayerClick(
      matchI18n('newAndUsedCarsPopoverTitle', i18nMessages),
      `${carlineGroup}~${carline}`,
    );
  };

  const popoverContent = (
    <StyledPopoverMaxWidthContainer>
      <Text
        variant={{ type: 'copy', order: '1', style: 'normal' }}
        margin={[0, 0, 'var(--spacing-relative-sm)', 0]}
      >
        {popoverTitle}
      </Text>
      <Text
        variant={{ type: 'copy', order: '2', style: 'normal' }}
        margin={[0, 0, 'var(--spacing-relative-sm)', 0]}
      >
        {popoverDescription}
      </Text>

      {newCars && newCars.amount > 0 && (
        <TextLink
          href={newCars.url}
          variant="standalone"
          size="sm"
          target="_self"
          margin={[0, 'var(--spacing-relative-sm)', 0, 0]}
          id="vtpTextLink"
          onClick={() => {
            trackCTAClick(
              newCars.url,
              `${carlineGroup}~${carline}`,
              matchI18n('newAndUsedCarsPopoverCTANewCarsMarketText', i18nMessages),
              'vtp new',
              newCars.amount,
            );
          }}
        >
          {newCarsMarketText}
        </TextLink>
      )}
      {usedCars && usedCars.amount > 0 && (
        <TextLink
          href={usedCars.url}
          variant="standalone"
          size="sm"
          id="vtpTextLink"
          onClick={() => {
            trackCTAClick(
              usedCars.url,
              `${carlineGroup}~${carline}`,
              matchI18n('newAndUsedCarsPopoverCTAUsedCarsMarketText', i18nMessages),
              'vtp used',
              usedCars.amount,
            );
          }}
        >
          {usedCarsMarketText}
        </TextLink>
      )}
    </StyledPopoverMaxWidthContainer>
  );

  return (
    <Popover
      aria-label={popoverTitle}
      content={popoverContent}
      id="vtp-popover"
      onClose={() => {
        setPopoverOpen(false);
      }}
      open={popoverOpen}
      withTip
      triggerElement={
        <TextLink
          variant="standalone"
          size="sm"
          onClick={handlePopoverOpen}
          aria-label={matchI18n('newAndUsedCarsCTAAriaLabel', i18nMessages, {
            value: carline,
          })}
        >
          {availableCarsInStockText}
        </TextLink>
      }
    />
  );
};

export default VtpPopoverPopover;
